<template>
  <v-card>
    <v-toolbar color="blue darken-3" dark dense flat>
      <v-icon class="mr-2">mdi-calendar-edit</v-icon>
      <v-toolbar-title class="subtitle-1 font-weight-bold">
        <div>Solicitudes — Fecha Comercialización</div>
        <div v-if="nombrePrograma" class="caption blue--text text--lighten-3" style="line-height:1.2; font-weight:400">
          {{ nombrePrograma }}
        </div>
      </v-toolbar-title>
      <v-spacer />
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-card-text class="pa-4">

      <!-- Fecha actual + botón nueva solicitud -->
      <div class="d-flex align-center pa-3 mb-3 rounded" style="background:#e3f2fd; border-left:4px solid #1565c0">
        <v-icon color="blue darken-3" class="mr-3">mdi-calendar-account</v-icon>
        <div>
          <div class="overline" style="font-size:10px;color:#546e7a">Fecha de Comercialización actual</div>
          <div class="body-2 font-weight-bold blue--text text--darken-3">
            {{ fechaMatriculaActual || 'Sin fecha asignada' }}
          </div>
        </div>
        <v-spacer />
        <v-tooltip bottom :disabled="puedeCrearSolicitud">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                small color="blue darken-3" dark
                :disabled="!puedeCrearSolicitud"
                @click="dialogSolicitud = true"
              >
                <v-icon left small>mdi-plus</v-icon>Nueva solicitud
              </v-btn>
            </span>
          </template>
          <span>{{ motivoRestriccion }}</span>
        </v-tooltip>
      </div>

      <div v-if="motivoRestriccion" class="caption orange--text text--darken-3 mb-3 d-flex align-center" style="gap:4px">
        <v-icon x-small color="orange darken-3">mdi-lock-outline</v-icon>
        {{ motivoRestriccion }}
      </div>

      <v-alert v-if="haySolicitudPendiente" type="info" dense outlined class="mb-3" icon="mdi-information-outline">
        Ya existe una solicitud <strong>pendiente de resolución</strong>.
      </v-alert>
      <v-alert v-else-if="tieneAprobada" type="success" dense outlined class="mb-3" icon="mdi-check-circle-outline">
        Ya existe una reprogramación <strong>aprobada</strong>. No se permiten más solicitudes.
      </v-alert>
      <v-alert v-else-if="hasSolicitudPrevia && !haySolicitudPendiente && !tieneAprobada" type="warning" dense outlined class="mb-3" icon="mdi-alert-outline">
        La única solicitud permitida ya fue utilizada (rechazada). No se puede volver a solicitar.
      </v-alert>

      <!-- Cargando -->
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="blue darken-3" />
        <div class="caption grey--text mt-2">Cargando historial...</div>
      </div>

      <!-- Sin historial -->
      <div v-else-if="historial.length === 0" class="text-center py-8 grey--text">
        <v-icon large color="grey lighten-2">mdi-calendar-question</v-icon>
        <div class="mt-2 caption">Sin solicitudes registradas para este programa.</div>
      </div>

      <!-- Timeline -->
      <v-timeline v-else dense clipped>
        <v-timeline-item
          v-for="sol in historial"
          :key="sol.id"
          :color="colorEstado(sol.estado)"
          small
        >
          <v-card outlined class="mb-2 rounded">
            <v-card-text class="py-2 px-3">
              <!-- Fila superior: estado + fecha + acciones -->
              <div class="d-flex align-center flex-wrap mb-2" style="gap:8px">
                <v-chip x-small :color="colorEstado(sol.estado)" dark>{{ sol.estado }}</v-chip>
                <span class="caption grey--text">{{ formatFecha(sol.created_at) }}</span>
                <v-spacer />
                <template v-if="sol.estado === 'SOLICITADO' || sol.estado === 'PENDIENTE'">
                  <!-- <v-btn
                    x-small color="green darken-1" dark
                    :loading="procesando === sol.id + '_ap'"
                    :disabled="procesando !== null"
                    @click="aprobar(sol)"
                  >
                    <v-icon left x-small>mdi-check</v-icon>Aprobar
                  </v-btn> -->
                  <!-- <v-btn
                    x-small color="red darken-1" dark
                    :loading="procesando === sol.id + '_re'"
                    :disabled="procesando !== null"
                    @click="abrirRechazar(sol)"
                  >
                    <v-icon left x-small>mdi-close</v-icon>Rechazar
                  </v-btn> -->
                </template>
              </div>

              <!-- Fecha solicitada -->
              <div class="d-flex align-center mb-1">
                <v-icon x-small color="blue darken-2" class="mr-1">mdi-calendar-arrow-right</v-icon>
                <span class="body-2">Fecha solicitada: <strong>{{ sol.fecha_matriculacion_solicitada }}</strong></span>
              </div>

              <!-- Descripción -->
              <div class="caption grey--text text--darken-1" v-if="sol.descripcion">
                <v-icon x-small class="mr-1">mdi-text-box-outline</v-icon>{{ sol.descripcion }}
              </div>

              <!-- Motivo de rechazo -->
              <div class="caption mt-1" v-if="sol.respuesta">
                <v-icon x-small color="red darken-1" class="mr-1">mdi-message-reply-text</v-icon>
                <span class="red--text text--darken-1">Motivo: {{ sol.respuesta }}</span>
              </div>

              <!-- Solicitante -->
              <div class="caption grey--text mt-1" v-if="sol.solicitante">
                <v-icon x-small class="mr-1">mdi-account-outline</v-icon>{{ sol.solicitante }}
              </div>
            </v-card-text>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </v-card-text>

    <!-- ── Dialog nueva solicitud ── -->
    <v-dialog v-model="dialogSolicitud" max-width="480" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="blue darken-3" class="mr-2">mdi-calendar-plus</v-icon>
          <span class="subtitle-1 font-weight-bold">Nueva Solicitud de Cambio</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1">
          <v-menu v-model="menuFecha" :close-on-content-click="false" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.fecha_matriculacion_solicitada"
                label="Nueva fecha de Comercialización *"
                prepend-inner-icon="mdi-calendar"
                readonly outlined dense
                v-bind="attrs"
                v-on="on"
                class="mb-3"
              />
            </template>
            <v-date-picker
              v-model="form.fecha_matriculacion_solicitada"
              @input="menuFecha = false"
              no-title locale="es"
              :min="fechaMinCalendario"
              :max="fechaMaxCalendario"
            />
          </v-menu>
          <div v-if="fechaMaxCalendario" class="caption blue--text text--darken-2 mb-3 d-flex align-center" style="gap:4px">
            <v-icon x-small color="blue darken-2">mdi-calendar-range</v-icon>
            La nueva fecha no puede exceder 15 días desde la fecha actual
            (máximo: <strong>{{ fechaMaxCalendario }}</strong>).
          </div>
          <v-textarea
            v-model="form.descripcion"
            label="Justificación *"
            outlined dense rows="3"
            placeholder="Explica el motivo del cambio de fecha..."
            counter="500"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="cerrarDialogSolicitud" :disabled="creando">Cancelar</v-btn>
          <v-btn
            color="blue darken-3" dark small
            :loading="creando"
            :disabled="!form.fecha_matriculacion_solicitada || !form.descripcion"
            @click="crearSolicitud"
          >
            <v-icon left small>mdi-send</v-icon>Enviar solicitud
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog rechazar ── -->
    <v-dialog v-model="dialogRechazar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="red darken-1" class="mr-2">mdi-close-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Rechazar solicitud</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1">
          <v-textarea
            v-model="formRechazar.respuesta"
            label="Motivo del rechazo *"
            outlined dense rows="3"
            placeholder="Indica el motivo por el que se rechaza la solicitud..."
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogRechazar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn
            color="red darken-1" dark small
            :loading="procesando !== null"
            :disabled="!formRechazar.respuesta"
            @click="rechazar"
          >
            <v-icon left small>mdi-close</v-icon>Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import xaxios from '@/xaxios';

const API = process.env.VUE_APP_API_REST;

export default {
  name: 'SolicitudFechaMatricula',

  props: {
    programaId: { type: [Number, String], required: true },
    nombrePrograma: { type: String, default: null },
    fechaMatricula: { type: String, default: null },
  },

  data() {
    return {
      fechaMatriculaActual: null,
      estadoPrograma: null,
      historial: [],
      loading: false,
      creando: false,
      procesando: null,
      dialogSolicitud: false,
      dialogRechazar: false,
      menuFecha: false,
      solicitudAgestionar: null,
      form: { fecha_matriculacion_solicitada: '', descripcion: '' },
      formRechazar: { respuesta: '' },
      snackbar: { show: false, msg: '', color: 'success' },
    };
  },

  computed: {
    haySolicitudPendiente() {
      return this.historial.some((s) => s.estado === 'SOLICITADO' || s.estado === 'PENDIENTE');
    },
    hasSolicitudPrevia() {
      return this.historial.length > 0;
    },
    tieneAprobada() {
      return this.historial.some((s) => s.estado === 'APROBADO');
    },
    diasHastaMatricula() {
      if (!this.fechaMatriculaActual) return null;
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const mat = new Date(this.fechaMatriculaActual.includes('T')
        ? this.fechaMatriculaActual
        : this.fechaMatriculaActual + 'T00:00:00');
      mat.setHours(0, 0, 0, 0);
      return Math.ceil((mat - hoy) / (1000 * 60 * 60 * 24));
    },
    puedeCrearSolicitud() {
      if (this.hasSolicitudPrevia) return false;
      if (this.estadoPrograma !== 'PROXIMO') return false;
      if (this.diasHastaMatricula === null) return false;
      return this.diasHastaMatricula > 5;
    },
    motivoRestriccion() {
      if (this.tieneAprobada)
        return 'Ya existe una reprogramación aprobada. Solo se permite una por programa.';
      if (this.hasSolicitudPrevia)
        return 'Ya se utilizó la única solicitud de cambio permitida para este programa.';
      if (this.estadoPrograma !== 'PROXIMO')
        return `El programa está en estado ${this.estadoPrograma || '—'}. Solo se puede solicitar cuando está en estado PRÓXIMO.`;
      if (this.diasHastaMatricula === null)
        return 'No hay fecha de comercialización registrada.';
      if (this.diasHastaMatricula < 0)
        return 'La fecha de comercialización ya pasó.';
      if (this.diasHastaMatricula <= 5)
        return `Faltan ${this.diasHastaMatricula} día(s) — ya no es posible solicitar (se requieren más de 5 días de anticipación). Las opciones son iniciar en fecha original o dar de baja.`;
      return null;
    },
    fechaMinCalendario() {
      return new Date().toISOString().slice(0, 10);
    },
    fechaMaxCalendario() {
      if (!this.fechaMatriculaActual) return null;
      const d = new Date(
        this.fechaMatriculaActual.includes('T')
          ? this.fechaMatriculaActual
          : this.fechaMatriculaActual + 'T00:00:00'
      );
      d.setDate(d.getDate() + 15);
      return d.toISOString().slice(0, 10);
    },
  },

  methods: {
    async cargarPrograma() {
      try {
        const { data } = await xaxios.get(
          `${process.env.VUE_APP_API_REST}/esam/programa/id/${this.programaId}`
        );
        this.fechaMatriculaActual = data.fecha_matricula || data.fecha_matriculacion
          || this.fechaMatricula || null;
        this.estadoPrograma = data.estado_programa_codigo
          || data.estado_programa?.codigo || null;
      } catch {
        this.fechaMatriculaActual = this.fechaMatricula || null;
        this.estadoPrograma = null;
      }
    },

    async cargarHistorial() {
      this.loading = true;
      try {
        const { data } = await xaxios.get(
          `${API}/api/v1/programa/${this.programaId}/fecha-matriculacion/solicitudes`
        );
        this.historial = Array.isArray(data.data?.historial) ? data.data.historial : [];
      } catch {
        this.historial = [];
      } finally {
        this.loading = false;
      }
    },

    async crearSolicitud() {
      if (this.fechaMaxCalendario && this.form.fecha_matriculacion_solicitada > this.fechaMaxCalendario) {
        this.notificar(`La fecha no puede exceder 15 días desde la fecha programada (máx: ${this.fechaMaxCalendario})`, 'warning');
        return;
      }
      this.creando = true;
      try {
        await xaxios.post(
          `${API}/api/v1/programa/${this.programaId}/fecha-matriculacion/solicitudes`,
          {
            fecha_matriculacion_solicitada: this.form.fecha_matriculacion_solicitada,
            descripcion: this.form.descripcion,
          }
        );
        this.notificar('Solicitud enviada correctamente', 'success');
        this.cerrarDialogSolicitud();
        await this.cargarHistorial();
        this.$emit('solicitud-creada');
      } catch (e) {
        const msg = e?.response?.data?.message || e?.response?.data?.error || 'Error al enviar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.creando = false;
      }
    },

    async aprobar(sol) {
      this.procesando = sol.id + '_ap';
      try {
        await xaxios.patch(
          `${API}/api/v1/programa/fecha-matriculacion/solicitudes/${sol.id}`,
          { estado: 'APROBADO' }
        );
        this.notificar('Solicitud aprobada. La fecha de matrícula fue actualizada.', 'success');
        await this.cargarHistorial();
        this.$emit('aprobada');
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para aprobar solicitudes'
          : e?.response?.data?.message || 'Error al aprobar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    abrirRechazar(sol) {
      this.solicitudAgestionar = sol;
      this.formRechazar.respuesta = '';
      this.dialogRechazar = true;
    },

    async rechazar() {
      this.procesando = this.solicitudAgestionar.id + '_re';
      try {
        await xaxios.patch(
          `${API}/api/v1/programa/fecha-matriculacion/solicitudes/${this.solicitudAgestionar.id}`,
          { estado: 'RECHAZADO', respuesta: this.formRechazar.respuesta }
        );
        this.notificar('Solicitud rechazada', 'warning');
        this.dialogRechazar = false;
        this.solicitudAgestionar = null;
        await this.cargarHistorial();
      } catch (e) {
        const msg = e?.response?.status === 403
          ? 'No tienes permiso para rechazar solicitudes'
          : e?.response?.data?.message || 'Error al rechazar la solicitud';
        this.notificar(msg, 'error');
      } finally {
        this.procesando = null;
      }
    },

    cerrarDialogSolicitud() {
      this.dialogSolicitud = false;
      this.form.fecha_matriculacion_solicitada = '';
      this.form.descripcion = '';
    },

    colorEstado(estado) {
      const map = { SOLICITADO: 'orange darken-2', PENDIENTE: 'orange darken-2', APROBADO: 'green darken-2', RECHAZADO: 'red darken-1' };
      return map[estado] || 'grey';
    },

    formatFecha(iso) {
      if (!iso) return '—';
      try {
        return new Date(iso).toLocaleDateString('es-BO', {
          day: '2-digit', month: 'short', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });
      } catch {
        return iso;
      }
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },

  mounted() {
    this.cargarPrograma();
    this.cargarHistorial();
  },
};
</script>
